import React, { useState } from 'react';

import { type QueryParams, loadQuery } from 'common/actions/ideaQueries';
import connect from 'common/core/connect';
import { getIdeaQuery } from 'common/subdomain/admin/v2/AdminV2IdeasTableV2/utils';
import IdeaSearchAncestors from 'common/subdomain/admin/v2/ideas/IdeaSearchAncestors';
import { Span } from 'common/ui/Text';
import SearchableList, { type SearchableListOption } from 'common/uiV3/Composite/SearchableList';
import Flex from 'common/uiV3/Flex';
import useDelayer from 'common/util/useDelayer';
import styles from 'css-module/components/subdomain/admin/v2/AdminV2IdeaDrawer/_IdeaSearch.module.scss';

import type { Idea } from 'common/api/endpoints/idea';
import type { IdeaIDQueriesState, IdeaQueryState } from 'common/reducers/ideaQueries';
import type { IdeasState } from 'common/reducers/ideas';
import Popover, { type PopoverContentProps } from 'common/uiV3/Popover';

type ConnectProps = {
  loadQuery: (queryParams: QueryParams) => Promise<void>;
};

type IdeaSearchBaseProps = {
  onSelectIdea: (idea: Idea) => void;
  filterResults?: (ideas: Idea[]) => Idea[];
};

type IdeaSearchPopoverProps = IdeaSearchBaseProps &
  Pick<PopoverContentProps, 'align' | 'side'> & {
    trigger: React.ReactNode;
  };

const UnconnectedIdeaSearchList = ({
  filterResults,
  loadQuery,
  onSelectIdea,
}: IdeaSearchBaseProps & ConnectProps) => {
  const [search, setSearch] = useState('');

  const delayedLoadQuery = useDelayer((value: string) => {
    if (value.trim()) {
      loadQuery({ search: value.trim() });
    }
  }, 250);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    delayedLoadQuery(value);
  };

  return (
    <IdeaSearchableList
      filterResults={filterResults}
      onSelectIdea={onSelectIdea}
      onSearchChange={handleSearchChange}
      search={search}
    />
  );
};

export const IdeaSearchList = connect(null, {
  loadQuery,
})(UnconnectedIdeaSearchList) as unknown as React.FC<IdeaSearchBaseProps>;

const IdeaSearchPopover = ({
  trigger,
  filterResults,
  onSelectIdea,
  side,
  align,
}: IdeaSearchPopoverProps) => (
  <Popover>
    <Popover.Trigger asChild>{trigger}</Popover.Trigger>
    <Popover.Content className="p-0 min-w-[300px]" side={side} align={align}>
      <IdeaSearchList filterResults={filterResults} onSelectIdea={onSelectIdea} />
    </Popover.Content>
  </Popover>
);

export default IdeaSearchPopover;

type IdeaSearchableListConnectProps = {
  searchQuery?: IdeaQueryState;
};
type IdeaSearchableListProps = {
  filterResults?: (ideas: Idea[]) => Idea[];
  onSelectIdea: (idea: Idea) => void;
  onSearchChange: (search: string) => void;
  search: string;
};

type IdeaSearchResultsProps = IdeaSearchableListProps & IdeaSearchableListConnectProps;

const UnconnectedIdeaSearchableList = ({
  filterResults,
  onSelectIdea,
  onSearchChange,
  search,
  searchQuery,
}: IdeaSearchResultsProps) => {
  const unfilteredIdeas = searchQuery?.items ?? [];
  const ideas = filterResults ? filterResults(unfilteredIdeas) : unfilteredIdeas;

  const options = ideas.map((idea) => ({
    value: idea._id,
    label: (
      <Flex className="flex-col gap-1">
        <IdeaSearchAncestors idea={idea} />
        <Span className={styles.ideaName} variant="bodyMd">
          {idea.title}
        </Span>
      </Flex>
    ),
    searchLabel: idea.title,
  }));

  const isSearching = Boolean(search && search.trim());
  const isLoading = isSearching && (!searchQuery || searchQuery.loading);
  const isError = isSearching && searchQuery && !searchQuery.items && searchQuery.error;
  const emptyPlaceholder = isError ? 'Something went wrong' : 'No ideas found';

  const handleOptionSelect = (option: SearchableListOption) => {
    const idea = ideas.find((i) => i._id === option.value);
    if (idea) {
      onSelectIdea(idea);
    }
  };

  return (
    <SearchableList
      emptyPlaceholder={emptyPlaceholder}
      hideEmptyStateUntilSearch
      isLoading={isLoading}
      isSearchable
      onOptionSelect={handleOptionSelect}
      onSearchChange={onSearchChange}
      options={options}
      searchPlaceholder="Search ideas"
    />
  );
};

const IdeaSearchableList = connect(
  (
    { ideas, ideaQueries }: { ideas: IdeasState; ideaQueries: IdeaIDQueriesState },
    { search }: IdeaSearchableListProps,
  ): IdeaSearchableListConnectProps => {
    if (!search || !search.trim()) {
      return {};
    }

    return {
      searchQuery: getIdeaQuery({
        ideas,
        ideaQueries,
        queryParams: { search },
      }),
    };
  },
)(UnconnectedIdeaSearchableList) as unknown as React.FC<IdeaSearchableListProps>;
